import { BlockType, type Vector3D } from '../types';

/** Wire-format payloads — stable so Socket.io can reuse them verbatim later. */
export interface PlayerMovePacket {
  playerId: string;
  position: Vector3D;
  velocity: Vector3D;
  yaw: number;
  health: number;
  maxHealth: number;
  sequence: number;
  clientTime: number;
}

export interface BlockChangePacket {
  playerId: string;
  x: number;
  y: number;
  z: number;
  blockType: BlockType;
  sequence: number;
  clientTime: number;
}

export interface PlayerDeathPacket {
  playerId: string;
  deathLocation: Vector3D;
  sequence: number;
  clientTime: number;
}

export type NetworkBridgeEvents = {
  connected: { playerId: string };
  disconnected: { reason: string };
  'peer:move': PlayerMovePacket;
  'peer:block': BlockChangePacket;
  'peer:death': PlayerDeathPacket;
};

type NetworkEventName = keyof NetworkBridgeEvents;
type NetworkHandler<E extends NetworkEventName> = (payload: NetworkBridgeEvents[E]) => void;

/**
 * Transport-agnostic multiplayer bridge.
 *
 * Today: local mock that echoes / fans out to registered peer handlers.
 * Tomorrow: replace the private `transport*` methods with Socket.io emits
 * (`this.socket.emit('player:move', packet)`) without changing call sites.
 *
 * Strict public API required by the game:
 *   - broadcastPlayerMove(position, velocity)
 *   - broadcastBlockChange(x, y, z, blockType)
 *   - broadcastPlayerDeath(playerId, deathLocation)
 */
export class NetworkBridge {
  private readonly localPlayerId: string;
  private readonly bus = new LocalEmitter<NetworkBridgeEvents>();
  private sequence = 0;
  private connected = false;

  /**
   * Optional Socket.io-shaped socket. When assigned, outbound packets go over
   * the wire instead of the in-process mock fan-out.
   *
   * Expected surface (compatible with socket.io-client):
   *   emit(event: string, payload: unknown): void
   *   on(event: string, handler: (...args: unknown[]) => void): void
   *   disconnect(): void
   */
  private socket: SocketLike | null = null;

  /** Simulated peer latency (ms) for the local mock — 0 disables delay. */
  mockLatencyMs = 0;

  constructor(localPlayerId: string) {
    this.localPlayerId = localPlayerId;
  }

  get playerId(): string {
    return this.localPlayerId;
  }

  get isConnected(): boolean {
    return this.connected;
  }

  /**
   * Bind a real Socket.io client. Call before `connect()`.
   * Incoming server events are remapped onto the typed local emitter.
   */
  attachSocket(socket: SocketLike): void {
    this.socket = socket;
    socket.on('player:move', (payload: unknown) => {
      this.bus.emit('peer:move', payload as PlayerMovePacket);
    });
    socket.on('player:block', (payload: unknown) => {
      this.bus.emit('peer:block', payload as BlockChangePacket);
    });
    socket.on('player:death', (payload: unknown) => {
      this.bus.emit('peer:death', payload as PlayerDeathPacket);
    });
    socket.on('disconnect', () => {
      this.connected = false;
      this.bus.emit('disconnected', { reason: 'socket-disconnect' });
    });
  }

  /** Open the session (mock: instant; socket: relies on existing connection). */
  connect(): void {
    if (this.connected) return;
    this.connected = true;
    this.bus.emit('connected', { playerId: this.localPlayerId });
  }

  disconnect(): void {
    if (!this.connected) return;
    this.connected = false;
    if (this.socket) {
      this.socket.disconnect();
    }
    this.bus.emit('disconnected', { reason: 'client-disconnect' });
  }

  on<E extends NetworkEventName>(event: E, handler: NetworkHandler<E>): () => void {
    return this.bus.on(event, handler);
  }

  // ---------------------------------------------------------------------------
  // Strict outbound API
  // ---------------------------------------------------------------------------

  broadcastPlayerMove(
    position: Vector3D,
    velocity: Vector3D,
    yaw = 0,
    health = 100,
    maxHealth = 100,
  ): void {
    if (!this.connected) return;
    const packet: PlayerMovePacket = {
      playerId: this.localPlayerId,
      position: { x: position.x, y: position.y, z: position.z },
      velocity: { x: velocity.x, y: velocity.y, z: velocity.z },
      yaw,
      health,
      maxHealth,
      sequence: ++this.sequence,
      clientTime: performance.now(),
    };
    this.transportEmit('player:move', packet);
  }

  broadcastBlockChange(x: number, y: number, z: number, blockType: BlockType): void {
    if (!this.connected) return;
    const packet: BlockChangePacket = {
      playerId: this.localPlayerId,
      x: x | 0,
      y: y | 0,
      z: z | 0,
      blockType,
      sequence: ++this.sequence,
      clientTime: performance.now(),
    };
    this.transportEmit('player:block', packet);
  }

  broadcastPlayerDeath(playerId: string, deathLocation: Vector3D): void {
    if (!this.connected) return;
    const packet: PlayerDeathPacket = {
      playerId,
      deathLocation: {
        x: deathLocation.x,
        y: deathLocation.y,
        z: deathLocation.z,
      },
      sequence: ++this.sequence,
      clientTime: performance.now(),
    };
    this.transportEmit('player:death', packet);
  }

  // ---------------------------------------------------------------------------
  // Transport seam — swap body for Socket.io later
  // ---------------------------------------------------------------------------

  private transportEmit(
    event: 'player:move' | 'player:block' | 'player:death',
    packet: PlayerMovePacket | BlockChangePacket | PlayerDeathPacket,
  ): void {
    if (this.socket) {
      this.socket.emit(event, packet);
      return;
    }
    // Mock: fan out to local peer listeners (skips echoing to self by default).
    this.mockFanOut(event, packet);
  }

  private mockFanOut(
    event: 'player:move' | 'player:block' | 'player:death',
    packet: PlayerMovePacket | BlockChangePacket | PlayerDeathPacket,
  ): void {
    const deliver = (): void => {
      switch (event) {
        case 'player:move':
          // Local mock does not re-emit own moves as peer moves.
          break;
        case 'player:block':
          this.bus.emit('peer:block', packet as BlockChangePacket);
          break;
        case 'player:death':
          this.bus.emit('peer:death', packet as PlayerDeathPacket);
          break;
      }
    };

    if (this.mockLatencyMs > 0) {
      setTimeout(deliver, this.mockLatencyMs);
    } else {
      deliver();
    }
  }
}

/** Minimal socket contract — satisfied by socket.io-client's Socket. */
export interface SocketLike {
  emit(event: string, payload: unknown): void;
  on(event: string, handler: (...args: unknown[]) => void): void;
  disconnect(): void;
}

/**
 * Private typed emitter (avoids coupling NetworkBridge to the engine EventBus
 * payload map). Same semantics, separate event namespace.
 */
class LocalEmitter<Events extends Record<string, unknown>> {
  private readonly listeners = new Map<keyof Events, Set<(payload: never) => void>>();

  on<E extends keyof Events>(event: E, handler: (payload: Events[E]) => void): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler as (payload: never) => void);
    return () => {
      set!.delete(handler as (payload: never) => void);
      if (set!.size === 0) this.listeners.delete(event);
    };
  }

  emit<E extends keyof Events>(event: E, payload: Events[E]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const handler of [...set]) {
      (handler as (payload: Events[E]) => void)(payload);
    }
  }
}

// Re-export BlockType for consumers that only import the bridge module.
export { BlockType };
